import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "active":
      return "bg-green-500"
    case "inactive":
      return "bg-yellow-500"
    case "suspended":
      return "bg-red-500"
    default:
      return "bg-gray-500"
  }
}

export function getEchartsMax(value: number, splitNumber = 6): number {
  if (value <= 0) return 1

  const minStep = value / splitNumber
  const step = getNiceStepAbove(minStep)
  return step * splitNumber
}

function getNiceStepAbove(minStep: number): number {
  const niceFactors = [1, 2, 5]
  const exponent = Math.floor(Math.log10(minStep))

  for (let exp = exponent; exp < exponent + 10; exp++) {
    const base = Math.pow(10, exp)
    for (const factor of niceFactors) {
      const step = factor * base
      if (step >= minStep) return step
    }
  }

  // fallback, shouldn't hit
  return Math.ceil(minStep)
}

export interface NiceScaleResult {
  min: number
  max: number
  step: number
  tickCount: number
  ticks: number[]
}

const NICE_FACTORS = [1.0, 2.0, 2.5, 5.0, 10.0]

function niceStep(roughStep: number): number {
  if (roughStep <= 0) return 1
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)))
  const fraction = roughStep / magnitude
  for (const factor of NICE_FACTORS) {
    if (fraction <= factor) return factor * magnitude
  }
  return 10 * magnitude
}

function buildScaleResult(
  min: number,
  max: number,
  step: number,
  tickCount: number,
): NiceScaleResult {
  const ticks: number[] = []
  for (let v = min; v <= max + step * 0.001; v += step) {
    ticks.push(Math.round(v / step) * step)
  }
  return { min, max, step, tickCount, ticks }
}

export function computeNiceScale(
  rawMin: number,
  rawMax: number,
  targetTicks = 5,
  exactTicks?: number,
): NiceScaleResult {
  if (rawMin === rawMax) {
    const ticks = exactTicks ?? targetTicks
    if (rawMin === 0) {
      const step = 10.0 / ticks
      return buildScaleResult(0, 10, step, ticks)
    }
    const fallback = Math.abs(rawMin) * 0.1
    const lo = rawMin >= 0 ? 0.0 : rawMin - fallback
    const hi = rawMax + fallback
    const step = (hi - lo) / ticks
    return buildScaleResult(lo, hi, step, ticks)
  }

  if (exactTicks !== undefined) {
    const niceMin = rawMin < 0 ? rawMin : 0.0
    const range = rawMax - niceMin
    const roughStep = range / exactTicks
    const resultStep = niceStep(roughStep)
    const finalMin = rawMin < 0 ? Math.floor(rawMin / resultStep) * resultStep : 0.0
    const finalMax = finalMin + resultStep * exactTicks
    return buildScaleResult(finalMin, finalMax, resultStep, exactTicks)
  }

  const niceMin = rawMin < 0 ? rawMin : 0.0
  const range = rawMax - niceMin
  const roughStep = range / targetTicks
  const resultStep = niceStep(roughStep)

  const floorMin = Math.floor(niceMin / resultStep) * resultStep
  const ceilMax = Math.ceil(rawMax / resultStep) * resultStep
  const count = Math.round((ceilMax - floorMin) / resultStep)

  return buildScaleResult(floorMin, ceilMax, resultStep, count)
}
